import React from 'react';
import type { DatasetAgentTrainingSelection } from '../../utils/datasetAgentsPreview';
import { DEFAULT_FINE_TUNING_BASE_MODELS } from '../../utils/trainableBaseModels';
import './DatasetAgentsPreviewPanel.css';

export interface DatasetAgentsPreviewPanelProps {
  selections: DatasetAgentTrainingSelection[];
  baseModelOptions: string[];
  minExamples?: number;
  loading?: boolean;
  error?: string | null;
  disabled?: boolean;
  agentReactions?: Record<string, { like: boolean; dislike: boolean }>;
  agentFilterFlags?: Record<string, { comment: boolean; groundtruth: boolean }>;
  onToggleReaction?: (agentName: string, type: 'like' | 'dislike') => void;
  onToggleFilterFlag?: (agentName: string, type: 'comment' | 'groundtruth') => void;
  onBulkSetReaction?: (type: 'like' | 'dislike', enabled: boolean) => void;
  onBulkSetFilterFlag?: (type: 'comment' | 'groundtruth', enabled: boolean) => void;
  onChange: (next: DatasetAgentTrainingSelection[]) => void;
}

function patchSelection(
  selections: DatasetAgentTrainingSelection[],
  rowKey: string,
  patch: Partial<DatasetAgentTrainingSelection>
): DatasetAgentTrainingSelection[] {
  return selections.map((row) => (row.rowKey === rowKey ? { ...row, ...patch } : row));
}

const DatasetAgentsPreviewPanel: React.FC<DatasetAgentsPreviewPanelProps> = ({
  selections,
  baseModelOptions,
  minExamples,
  loading = false,
  error = null,
  disabled = false,
  agentReactions = {},
  agentFilterFlags = {},
  onToggleReaction,
  onToggleFilterFlag,
  onBulkSetReaction,
  onBulkSetFilterFlag,
  onChange,
}) => {
  if (loading) {
    return <p className="dataset-agents-preview__empty">Loading agents from selected datasets…</p>;
  }

  if (error) {
    return (
      <p className="dataset-agents-preview__error" role="alert">
        {error}
      </p>
    );
  }

  if (selections.length === 0) {
    return (
      <p className="dataset-agents-preview__empty">
        No agents found in the selected datasets
        {minExamples != null ? ` (minimum ${minExamples} examples required per agent)` : ''}.
      </p>
    );
  }

  const modelOptions =
    baseModelOptions.length > 0 ? baseModelOptions : [...DEFAULT_FINE_TUNING_BASE_MODELS];

  const showBulkActions =
    !disabled &&
    selections.length > 0 &&
    (onBulkSetReaction != null || onBulkSetFilterFlag != null);

  return (
    <div className="dataset-agents-preview">
      <p className="dataset-agents-preview__hint">
        Agents are discovered from the selected datasets. Missing agents are not in the current integration
        config — choose a base model before preview/start. Like, dislike, comment, and ground-truth filters can
        be set for every agent; only selected agents with enough examples are included in preview and training.
      </p>
      {showBulkActions ? (
        <div className="dataset-agents-preview__bulk">
          <span className="dataset-agents-preview__bulk-label">Signals for all agents:</span>
          {onBulkSetReaction ? (
            <>
              <button
                type="button"
                className="dataset-agents-preview__bulk-btn dataset-agents-preview__bulk-btn--like-on"
                onClick={() => onBulkSetReaction('like', true)}
              >
                <i className="fas fa-thumbs-up" aria-hidden />
                Likes on
              </button>
              <button
                type="button"
                className="dataset-agents-preview__bulk-btn dataset-agents-preview__bulk-btn--muted"
                onClick={() => onBulkSetReaction('like', false)}
              >
                Likes off
              </button>
              <button
                type="button"
                className="dataset-agents-preview__bulk-btn dataset-agents-preview__bulk-btn--dislike-on"
                onClick={() => onBulkSetReaction('dislike', true)}
              >
                <i className="fas fa-thumbs-down" aria-hidden />
                Dislikes on
              </button>
              <button
                type="button"
                className="dataset-agents-preview__bulk-btn dataset-agents-preview__bulk-btn--muted"
                onClick={() => onBulkSetReaction('dislike', false)}
              >
                Dislikes off
              </button>
            </>
          ) : null}
          {onBulkSetFilterFlag ? (
            <>
              <button
                type="button"
                className="dataset-agents-preview__bulk-btn dataset-agents-preview__bulk-btn--comment-on"
                onClick={() => onBulkSetFilterFlag('comment', true)}
              >
                <i className="fas fa-comment-dots" aria-hidden />
                Comments on
              </button>
              <button
                type="button"
                className="dataset-agents-preview__bulk-btn dataset-agents-preview__bulk-btn--muted"
                onClick={() => onBulkSetFilterFlag('comment', false)}
              >
                Comments off
              </button>
              <button
                type="button"
                className="dataset-agents-preview__bulk-btn dataset-agents-preview__bulk-btn--groundtruth-on"
                onClick={() => onBulkSetFilterFlag('groundtruth', true)}
              >
                <i className="fas fa-check-double" aria-hidden />
                Groundtruth on
              </button>
              <button
                type="button"
                className="dataset-agents-preview__bulk-btn dataset-agents-preview__bulk-btn--muted"
                onClick={() => onBulkSetFilterFlag('groundtruth', false)}
              >
                Groundtruth off
              </button>
            </>
          ) : null}
        </div>
      ) : null}
      <div className="dataset-agents-preview__table-wrap">
        <table className="dataset-agents-preview__table">
          <thead>
            <tr>
              <th aria-label="Include in training" />
              <th>Agent</th>
              <th>Examples</th>
              <th>Base model</th>
              <th>Signals</th>
            </tr>
          </thead>
          <tbody>
            {selections.map((row) => {
              const rowDisabled = disabled || !row.eligible_for_training;
              const rowBaseModelDisabled =
                disabled || (!row.isMissing && (!row.eligible_for_training || !row.selected));
              const checked = row.selected && row.eligible_for_training;
              const needsBaseModel = row.selected && row.eligible_for_training && !row.base_model.trim();
              const signalDisabled = disabled;
              const reactions = agentReactions[row.agent_name] ?? { like: true, dislike: true };
              const flags = agentFilterFlags[row.agent_name] ?? { comment: true, groundtruth: true };
              return (
                <tr
                  key={row.rowKey}
                  className={[
                    row.isMissing ? 'dataset-agents-preview__row--missing' : '',
                    !row.eligible_for_training ? 'dataset-agents-preview__row--ineligible' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <td>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={rowDisabled}
                      aria-label={`Include ${row.agent_name} in fine-tuning`}
                      onChange={(e) => {
                        if (!row.eligible_for_training) return;
                        onChange(
                          patchSelection(selections, row.rowKey, { selected: e.target.checked })
                        );
                      }}
                    />
                  </td>
                  <td>
                    <div className="dataset-agents-preview__agent-cell">
                      <strong>{row.agent_name}</strong>
                      {row.isMissing ? (
                        <span className="dataset-agents-preview__badge">Not in current config</span>
                      ) : null}
                      {!row.eligible_for_training ? (
                        <span className="dataset-agents-preview__badge dataset-agents-preview__badge--warn">
                          Too few examples
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td>
                    <span className="dataset-agents-preview__examples">
                      {row.total_examples} total
                      <span className="dataset-agents-preview__examples-detail">
                        {row.train_examples} train · {row.validation_examples} val · {row.test_examples} test
                      </span>
                    </span>
                  </td>
                  <td>
                    <select
                      className={`dataset-agents-preview__select${
                        needsBaseModel ? ' dataset-agents-preview__select--invalid' : ''
                      }`}
                      value={row.base_model || modelOptions[0] || ''}
                      disabled={rowBaseModelDisabled}
                      onChange={(e) =>
                        onChange(
                          patchSelection(selections, row.rowKey, { base_model: e.target.value })
                        )
                      }
                    >
                      {modelOptions.map((model) => (
                        <option key={model} value={model}>
                          {model}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <div className="dataset-agents-preview__signals" aria-label={`Training signals for ${row.agent_name}`}>
                      <button
                        type="button"
                        className={`dataset-agents-preview__signal dataset-agents-preview__signal--like${reactions.like ? ' dataset-agents-preview__signal--active' : ''}`}
                        disabled={signalDisabled || !onToggleReaction}
                        onClick={() => onToggleReaction?.(row.agent_name, 'like')}
                        title="Use likes for training"
                      >
                        <i className="fas fa-thumbs-up" aria-hidden />
                        <span>Like</span>
                      </button>
                      <button
                        type="button"
                        className={`dataset-agents-preview__signal dataset-agents-preview__signal--dislike${reactions.dislike ? ' dataset-agents-preview__signal--active' : ''}`}
                        disabled={signalDisabled || !onToggleReaction}
                        onClick={() => onToggleReaction?.(row.agent_name, 'dislike')}
                        title="Use dislikes for training"
                      >
                        <i className="fas fa-thumbs-down" aria-hidden />
                        <span>Dislike</span>
                      </button>
                      <button
                        type="button"
                        className={`dataset-agents-preview__signal dataset-agents-preview__signal--comment${flags.comment ? ' dataset-agents-preview__signal--active' : ''}`}
                        disabled={signalDisabled || !onToggleFilterFlag}
                        onClick={() => onToggleFilterFlag?.(row.agent_name, 'comment')}
                        title="Use comments for training"
                      >
                        <i className="fas fa-comment-dots" aria-hidden />
                        <span>Comment</span>
                      </button>
                      <button
                        type="button"
                        className={`dataset-agents-preview__signal dataset-agents-preview__signal--groundtruth${flags.groundtruth ? ' dataset-agents-preview__signal--active' : ''}`}
                        disabled={signalDisabled || !onToggleFilterFlag}
                        onClick={() => onToggleFilterFlag?.(row.agent_name, 'groundtruth')}
                        title="Use ground truth for training"
                      >
                        <i className="fas fa-check-double" aria-hidden />
                        <span>Groundtruth</span>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DatasetAgentsPreviewPanel;
